import { useCallback, useEffect, useMemo, useState } from "react"
import { partition } from "lodash"

import { db } from "../firebase"
import ticketRequiresAttention from "../utils/ticketRequiresAttention"
import { TicketAssignee } from "../constants/enums"

export const LIMIT_BATCH_SIZE = 50

/**
 * The realtime listener covers the first window only. Additional pages are
 * fetched once via startAfter and kept as static documents.
 *
 * The previous implementation grew the listener's own limit instead, which
 * tore down the listener and rebuilt it on every step. Firestore bills a
 * listener's initial snapshot in full, so walking a column to N documents
 * cost 50+100+150+...+N reads. It now costs one read per document.
 */
const MAX_AUTO_PAGES = 10
const MAX_LOAD_ALL_PAGES = 60

type TagDatum = { name: string, id: string }

/**
 * Resolves user typed tag filters to tag ids when tag metadata is available.
 * Without metadata the raw input is passed through, matching the behaviour of
 * the diesel column which never had tag ids to resolve against.
 */
function resolveTagFilters (
  tagFilters: string[],
  tagData?: TagDatum[],
): string[] {
  if (!tagData) {
    return tagFilters
  }

  const tagDataName = tagData.map(({ name }) => name)

  return tagFilters.map((userInput) => {
    const tagNameIndex = tagDataName.reduce((acc, tagName, index) => {
      if (tagName.startsWith(userInput) || tagName === userInput) {
        acc = index
      }
      return acc
    }, -1)

    return tagNameIndex > -1 ? tagData[tagNameIndex].id : userInput
  })
}

export default function useTicketColumn (
  { assignedGroup, claimType, tagFilters = [], tagData, skipLeads = false }:
  {
    assignedGroup: string
    claimType: string
    tagFilters?: string[]
    tagData?: TagDatum[]
    skipLeads?: boolean
  },
) {
  const [liveDocs, setLiveDocs] = useState<any[]>([])
  const [extraDocs, setExtraDocs] = useState<any[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [baseExhausted, setBaseExhausted] = useState(false)
  const [tailExhausted, setTailExhausted] = useState(false)
  const [pagesLoaded, setPagesLoaded] = useState(0)
  const [shouldLoadAll, setShouldLoadAll] = useState(false)

  // Serialized so an unstable array reference from the parent cannot rebuild
  // the listener on every render.
  const filterKey = JSON.stringify(tagFilters)
  const tagDataKey = JSON.stringify(tagData || null)

  const buildQuery = useCallback(() => {
    let q = db
      .collection("tickets")
      .where("assignedGroup", "==", assignedGroup)
      .where("type", "==", claimType)
      .orderBy("claimLastUpdated", "desc")

    const parsedFilters = resolveTagFilters(
      JSON.parse(filterKey),
      JSON.parse(tagDataKey) || undefined,
    )

    if (parsedFilters.length) {
      q = q.where("tags", "array-contains-any", parsedFilters)
    }

    return q
  }, [assignedGroup, claimType, filterKey, tagDataKey])

  // Realtime on the first window only.
  useEffect(() => {
    setExtraDocs([])
    setPagesLoaded(0)
    setBaseExhausted(false)
    setTailExhausted(false)
    setLoaded(false)

    return buildQuery()
      .limit(LIMIT_BATCH_SIZE)
      .onSnapshot((snap) => {
        setLiveDocs(snap.docs)
        setBaseExhausted(snap.size < LIMIT_BATCH_SIZE)
        setLoaded(true)
      })
  }, [buildQuery])

  const exhausted = baseExhausted || tailExhausted

  const allDocs = useMemo(
    () => liveDocs.concat(extraDocs),
    [liveDocs, extraDocs],
  )

  const { awakeDocs, snoozedDocs } = useMemo(() => {
    const relevant = skipLeads
      ? allDocs.filter(doc => doc.data()?.isLead !== true)
      : allDocs

    const [awake, snoozed] = partition(
      relevant,
      doc => ticketRequiresAttention((doc.data() || {}) as Ticket),
    )

    return { awakeDocs: awake, snoozedDocs: snoozed }
  }, [allDocs, skipLeads])

  const loadMore = useCallback(async () => {
    if (loadingMore || exhausted) {
      return
    }
    setLoadingMore(true)

    try {
      const cursor = allDocs[allDocs.length - 1]
      if (!cursor) {
        setTailExhausted(true)
        return
      }

      const snap = await buildQuery()
        .startAfter(cursor)
        .limit(LIMIT_BATCH_SIZE)
        .get()

      if (snap.size < LIMIT_BATCH_SIZE) {
        setTailExhausted(true)
      }
      if (snap.size) {
        setExtraDocs(prev => prev.concat(snap.docs))
      }
      setPagesLoaded(prev => prev + 1)
    }
    finally {
      setLoadingMore(false)
    }
  }, [allDocs, buildQuery, exhausted, loadingMore])

  // Auto-advance when the column shows nothing actionable. Runs in an effect
  // rather than during render, and is capped so a column of permanently
  // snoozed tickets cannot walk the whole collection.
  useEffect(() => {
    if (!loaded || loadingMore || exhausted) {
      return
    }

    if (shouldLoadAll) {
      if (pagesLoaded < MAX_LOAD_ALL_PAGES) {
        loadMore()
      }
      return
    }

    if (
      awakeDocs.length === 0 &&
      assignedGroup !== TicketAssignee.COMPLETE &&
      pagesLoaded < MAX_AUTO_PAGES
    ) {
      loadMore()
    }
  }, [
    loaded,
    loadingMore,
    exhausted,
    shouldLoadAll,
    pagesLoaded,
    awakeDocs.length,
    assignedGroup,
    loadMore,
  ])

  return {
    ticketDocs: awakeDocs.concat(snoozedDocs),
    awakeCount: awakeDocs.length,
    snoozedCount: snoozedDocs.length,
    hasMore: !exhausted,
    loadingMore,
    loadMore,
    shouldLoadAll,
    setShouldLoadAll,
  }
}
